import { useState, useEffect, useCallback } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  RefreshCw, AlertTriangle, Clock, CheckCircle, Eye, Scale,
  Upload, FileText, Truck, Loader2, Search,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API}/api/v2/recon`;
const D2B_BASE = `${API}/api/v2/delhivery-b2b-recon`;

const STATUS_BADGE = {
  UPLOADED: "bg-blue-100 text-blue-700",
  WEIGHT_MISMATCH: "bg-orange-100 text-orange-700",
  DISPUTED: "bg-red-100 text-red-700",
  REWEIGH_PENDING: "bg-amber-100 text-amber-700",
  SETTLED: "bg-emerald-100 text-emerald-700",
  CLOSED: "bg-slate-200 text-slate-700",
};

function fmt(n) {
  return `\u20B9${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function ReconciliationV2() {
  const token = localStorage.getItem("sendtoo_token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const [tab, setTab] = useState("own");
  const [stats, setStats] = useState({
    pending_review: 0, weight_mismatches: 0, active_disputes: 0, settled_today: 0,
  });
  const [recons, setRecons] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(false);

  // Proof modal
  const [viewRecon, setViewRecon] = useState(null);

  // Reweigh form
  const [reweighOpen, setReweighOpen] = useState(false);
  const [reweighFor, setReweighFor] = useState(null);
  const [rwWeight, setRwWeight] = useState("");
  const [rwPhoto, setRwPhoto] = useState("");

  // Dispute form
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [disputeFor, setDisputeFor] = useState(null);
  const [dReason, setDReason] = useState("");
  const [dWeight, setDWeight] = useState("");

  // 3PL sync
  const [vendor, setVendor] = useState("DELHIVERY");
  const [awbInput, setAwbInput] = useState("");
  const [syncResults, setSyncResults] = useState([]);
  const [syncLoading, setSyncLoading] = useState(false);

  // Manual CSV
  const [csvRows, setCsvRows] = useState([]);

  // POD
  const [podSearch, setPodSearch] = useState("");
  const [d2bLrInput, setD2bLrInput] = useState("");
     const [d2bChecking, setD2bChecking] = useState(false);
     const [d2bList, setD2bList] = useState([]);
     const [d2bLoading, setD2bLoading] = useState(false);
  const [podShipment, setPodShipment] = useState(null);
  const [podOrderType, setPodOrderType] = useState("B2C");
  const [podDeliveryUrl, setPodDeliveryUrl] = useState("");
  const [podOtp, setPodOtp] = useState("");
  const [podPhone, setPodPhone] = useState("");
  const [podSignedUrl, setPodSignedUrl] = useState("");
  const [podStampedUrl, setPodStampedUrl] = useState("");
  const [podSubmitting, setPodSubmitting] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/summary-stats`, { headers });
      if (r.ok) setStats(await r.json());
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRecons = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BASE}/list`, { headers });
      if (r.ok) setRecons(await r.json());
    } catch { /* ignore */ }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDisputes = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BASE}/disputes`, { headers });
      if (r.ok) setDisputes(await r.json());
    } catch { /* ignore */ }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => {
    if (tab === "own") fetchRecons();
    if (tab === "disputes") fetchDisputes();
  }, [tab, fetchRecons, fetchDisputes]);

  const refreshAll = () => {
    fetchStats();
    if (tab === "own") fetchRecons();
    if (tab === "disputes") fetchDisputes();
  };

  const doReweigh = async () => {
    if (!reweighFor || !rwWeight) return toast.error("Enter reweigh weight");
    try {
      const r = await fetch(`${BASE}/${reweighFor.shipment_id}/reweigh`, {
        method: "POST", headers,
        body: JSON.stringify({
          reweighed_weight_kg: parseFloat(rwWeight),
          reweigh_photo_url: rwPhoto || null,
        }),
      });
      if (r.ok) {
        const d = await r.json();
        toast.success(`Reweigh recorded. Δ ${fmt(d.difference)}`);
        setReweighOpen(false); setRwWeight(""); setRwPhoto("");
        refreshAll();
      } else {
        toast.error("Reweigh failed");
      }
    } catch (e) { toast.error(e.message); }
  };

  const doDispute = async () => {
    if (!disputeFor || !dReason) return toast.error("Enter a dispute reason");
    try {
      const r = await fetch(`${BASE}/${disputeFor.shipment_id}/dispute`, {
        method: "POST", headers,
        body: JSON.stringify({
          dispute_reason: dReason,
          disputed_weight_kg: parseFloat(dWeight) || disputeFor.chargeable_weight_kg,
        }),
      });
      if (r.ok) {
        toast.success("Dispute raised");
        setDisputeOpen(false); setDReason(""); setDWeight("");
        refreshAll();
      } else {
        toast.error("Failed");
      }
    } catch (e) { toast.error(e.message); }
  };

  const runSync = async () => {
    const awbs = awbInput.split(/[\s,]+/).map((x) => x.trim()).filter(Boolean);
    if (awbs.length === 0) return toast.error("Enter at least one AWB");
    setSyncLoading(true);
    try {
      const r = await fetch(`${BASE}/3pl/sync`, {
        method: "POST", headers,
        body: JSON.stringify({ awb_numbers: awbs, vendor }),
      });
      if (r.ok) {
        const d = await r.json();
        setSyncResults(d.details || []);
        toast.success(`Synced ${d.synced} | Disputed ${d.disputed} | Matched ${d.matched}`);
        fetchStats();
      } else {
        toast.error("Sync failed");
      }
    } catch (e) { toast.error(e.message); }
    setSyncLoading(false);
  };
  const fetchD2bList = async () => {
    setD2bLoading(true);
    try {
      const r = await fetch(`${D2B_BASE}/list`, { headers });
      if (r.ok) setD2bList(await r.json());
    } catch (e) { toast.error(e.message); }
    setD2bLoading(false);
  };

  const checkD2bLr = async () => {
    const lrnum = d2bLrInput.trim();
    if (!lrnum) return toast.error("Enter an LR number");
    setD2bChecking(true);
    try {
      const r = await fetch(`${D2B_BASE}/check`, {
        method: "POST", headers,
        body: JSON.stringify({ lrnum }),
      });
      if (r.ok) {
        const d = await r.json();
        toast.success(d.status === "PENDING_REVIEW" ? "Discrepancy found — staged for review" : "No discrepancy found");
        setD2bLrInput("");
        fetchD2bList();
      } else {
        const err = await r.json();
        toast.error(err.detail || "Check failed");
      }
    } catch (e) { toast.error(e.message); }
    setD2bChecking(false);
  };

  const approveD2b = async (lrnum) => {
    try {
      const r = await fetch(`${D2B_BASE}/${lrnum}/approve`, { method: "POST", headers });
      if (r.ok) {
        const d = await r.json();
        toast.success(d.wallet_adjusted ? `Approved — wallet adjusted by ₹${d.differential}` : "Approved, but wallet adjustment failed — check logs");
        fetchD2bList();
      } else {
        toast.error("Approval failed");
      }
    } catch (e) { toast.error(e.message); }
  };

  const onCsvFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const lines = text.split(/\r?\n/).filter(Boolean);
      const header = lines[0].split(",").map((s) => s.trim().toLowerCase());
      const idx = {
        awb: header.indexOf("awb"),
        our: header.indexOf("our_weight_kg"),
        carr: header.indexOf("carrier_weight_kg"),
        name: header.indexOf("carrier_name"),
      };
      const rows = lines.slice(1).map((line) => {
        const parts = line.split(",").map((s) => s.trim());
        return {
          awb: parts[idx.awb],
          our_weight_kg: parseFloat(parts[idx.our]) || 0,
          carrier_weight_kg: parseFloat(parts[idx.carr]) || 0,
          carrier_name: parts[idx.name] || "MANUAL",
        };
      });
      setCsvRows(rows);
    };
    reader.readAsText(file);
  };

  const submitCsv = async () => {
    if (csvRows.length === 0) return toast.error("No rows loaded");
    // Group by carrier name and call sync per carrier
    const byCarrier = {};
    csvRows.forEach((r) => {
      const k = (r.carrier_name || "MANUAL").toUpperCase();
      (byCarrier[k] = byCarrier[k] || []).push(r.awb);
    });
    try {
      let total = 0, disp = 0, match = 0;
      for (const [carrier, awbs] of Object.entries(byCarrier)) {
        const r = await fetch(`${BASE}/3pl/sync`, {
          method: "POST", headers,
          body: JSON.stringify({ awb_numbers: awbs, vendor: carrier }),
        });
        if (r.ok) {
          const d = await r.json();
          total += d.synced; disp += d.disputed; match += d.matched;
        }
      }
      toast.success(`CSV synced: ${total} rows | Disputed ${disp} | Matched ${match}`);
      setCsvRows([]);
      fetchStats();
    } catch (e) { toast.error(e.message); }
  };

  const searchPodShipment = async () => {
    if (!podSearch.trim()) return;
    try {
      const r = await fetch(`${API}/api/shipments?awb=${encodeURIComponent(podSearch.trim())}`, { headers });
      if (r.ok) {
        const d = await r.json();
        const items = d.items || (Array.isArray(d) ? d : []);
        if (items.length === 0) return toast.error("Shipment not found");
        const s = items[0];
        setPodShipment(s);
        setPodPhone(s.destination?.phone || s.customer_phone || "");
        setPodOrderType(s.order_type || "B2C");
      }
    } catch (e) { toast.error(e.message); }
  };

  const submitPod = async () => {
    if (!podShipment) return;
    setPodSubmitting(true);
    try {
      const body = { order_type: podOrderType };
      if (podOrderType === "B2C") {
        body.delivery_photo_url = podDeliveryUrl;
        body.otp_code = podOtp;
        body.customer_phone = podPhone;
      } else {
        body.signed_doc_url = podSignedUrl;
        body.stamped_photo_url = podStampedUrl;
      }
      const r = await fetch(`${BASE}/${podShipment.id}/pod`, {
        method: "POST", headers, body: JSON.stringify(body),
      });
      if (r.ok) {
        const d = await r.json();
        if (d.verified) {
          toast.success("Delivery confirmed. POD recorded.");
          setPodShipment(null); setPodSearch("");
          setPodDeliveryUrl(""); setPodOtp(""); setPodPhone("");
          setPodSignedUrl(""); setPodStampedUrl("");
          refreshAll();
        } else {
          toast.error(d.message || "POD verification failed");
        }
      }
    } catch (e) { toast.error(e.message); }
    setPodSubmitting(false);
  };

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="recon-v2">
        {/* HEADER */}
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Reconciliation V2</h1>
          <p className="text-sm text-slate-500">Per-shipment weight verification and dispute management</p>
        </div>

        {/* SUMMARY CARDS */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="summary-cards">
          <StatCard icon={Clock} label="Pending Review" value={stats.pending_review} color="text-blue-600" />
          <StatCard icon={Scale} label="Weight Mismatches" value={stats.weight_mismatches} color="text-orange-600" />
          <StatCard icon={AlertTriangle} label="Active Disputes" value={stats.active_disputes} color="text-red-600" />
          <StatCard icon={CheckCircle} label="Settled Today" value={stats.settled_today} color="text-emerald-600" />
        </div>

        {/* TABS */}
        <div className="flex border-b border-slate-200">
          {[
            { k: "own", label: "Own Network", icon: Truck },
            { k: "delhivery_b2b", label: "Delhivery B2B", icon: Scale },
            { k: "3pl", label: "3PL Sync", icon: RefreshCw },
            { k: "disputes", label: "Disputes", icon: AlertTriangle },
          ].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
                    className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                      tab === t.k ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500 hover:text-slate-700"
                    }`}>
              <t.icon className="w-4 h-4 inline-block mr-1.5" /> {t.label}
            </button>
          ))}
        </div>

        {/* TAB 1: OWN */}
        {tab === "own" && (
          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Shipment</th>
                    <th className="text-left px-3 py-2 font-semibold">Declared kg</th>
                    <th className="text-left px-3 py-2 font-semibold">Actual kg</th>
                    <th className="text-left px-3 py-2 font-semibold">Final kg</th>
                    <th className="text-right px-3 py-2 font-semibold">Difference</th>
                    <th className="text-left px-3 py-2 font-semibold">Status</th>
                    <th className="text-right px-3 py-2 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={7} className="py-10 text-center">
                      <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />
                    </td></tr>
                  ) : recons.length === 0 ? (
                    <tr><td colSpan={7} className="py-10 text-center text-slate-400">No recon records</td></tr>
                  ) : recons.map((r) => (
                    <tr key={r.shipment_id} className="border-b border-slate-100 hover:bg-slate-50/50"
                        data-testid={`recon-row-${r.shipment_id}`}>
                      <td className="px-3 py-2 font-mono text-[11px]">{r.shipment_id}</td>
                      <td className="px-3 py-2">{r.declared_weight_kg ?? "—"}</td>
                      <td className="px-3 py-2">{r.actual_weight_kg ?? "—"}</td>
                      <td className="px-3 py-2 font-medium">
                        {r.final_chargeable_weight ?? r.chargeable_weight_kg ?? "—"}
                      </td>
                      <td className={`px-3 py-2 text-right font-medium ${
                        (r.weight_difference || 0) > 0 ? "text-red-600" :
                        (r.weight_difference || 0) < 0 ? "text-emerald-600" : "text-slate-500"
                      }`}>
                        {r.weight_difference != null ? `${r.weight_difference > 0 ? "+" : ""}${r.weight_difference}` : "—"}
                      </td>
                      <td className="px-3 py-2">
                        <Badge className={`text-[10px] ${STATUS_BADGE[r.recon_status] || "bg-slate-100"}`}>
                          {r.recon_status || "—"}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button variant="outline" size="sm" onClick={() => setViewRecon(r)}
                                  data-testid={`view-proof-${r.shipment_id}`}>
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                          {r.recon_status === "WEIGHT_MISMATCH" && (
                            <Button variant="outline" size="sm"
                                    onClick={() => { setDisputeFor(r); setDWeight(String(r.chargeable_weight_kg || "")); setDisputeOpen(true); }}
                                    data-testid={`dispute-${r.shipment_id}`}
                                    className="text-red-600 border-red-200">
                              Dispute
                            </Button>
                          )}
                          {r.recon_status === "DISPUTED" && (
                            <Button size="sm"
                                    onClick={() => { setReweighFor(r); setRwWeight(""); setRwPhoto(""); setReweighOpen(true); }}
                                    data-testid={`reweigh-${r.shipment_id}`}
                                    className="bg-amber-500 hover:bg-amber-600">
                              Reweigh
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TAB 2: 3PL */}
        {tab === "3pl" && (
          <div className="space-y-4">
            <div className="bg-white border rounded-lg p-4">
              <h3 className="text-sm font-bold text-slate-700 mb-3">3PL Weight Reconciliation</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end mb-3">
                <div>
                  <label className="text-[10px] uppercase text-slate-500 font-semibold">Vendor</label>
                  <select value={vendor} onChange={(e) => setVendor(e.target.value)}
                          className="w-full border border-slate-300 rounded h-9 px-2 text-sm"
                          data-testid="sync-vendor">
                    <option value="DELHIVERY">DELHIVERY</option>
                    <option value="BLUEDART">BLUEDART</option>
                    <option value="INDIAPOST">INDIA POST</option>
                  </select>
                </div>
                <div className="md:col-span-3">
                  <label className="text-[10px] uppercase text-slate-500 font-semibold">AWB Numbers (one per line)</label>
                  <Textarea rows={4} value={awbInput} onChange={(e) => setAwbInput(e.target.value)}
                            placeholder="AWB0001&#10;AWB0002&#10;AWB0003"
                            data-testid="sync-awbs" />
                </div>
              </div>
              <Button onClick={runSync} disabled={syncLoading} className="bg-indigo-600 hover:bg-indigo-700"
                      data-testid="run-sync">
                {syncLoading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
                Sync with {vendor}
              </Button>

              {syncResults.length > 0 && (
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="text-left px-2 py-2">AWB</th>
                        <th className="text-right px-2 py-2">Our Weight</th>
                        <th className="text-right px-2 py-2">3PL Weight</th>
                        <th className="text-right px-2 py-2">Difference</th>
                        <th className="text-left px-2 py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {syncResults.map((row, i) => (
                        <tr key={i} className={`border-b ${
                          row.status === "DISPUTED" ? "bg-red-50" : "bg-emerald-50/50"
                        }`} data-testid={`sync-row-${i}`}>
                          <td className="px-2 py-2 font-mono text-[11px]">{row.awb}</td>
                          <td className="px-2 py-2 text-right">{row.our_weight_kg} kg</td>
                          <td className="px-2 py-2 text-right">{row.vendor_weight_kg} kg</td>
                          <td className={`px-2 py-2 text-right font-medium ${
                            row.difference > 0 ? "text-red-600" : "text-emerald-600"
                          }`}>
                            {row.difference > 0 ? "+" : ""}{row.difference} kg
                          </td>
                          <td className="px-2 py-2">
                            <Badge className={`text-[10px] ${
                              row.status === "DISPUTED" ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"
                            }`}>{row.status}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="bg-white border rounded-lg p-4">
              <h3 className="text-sm font-bold text-slate-700 mb-1">Manual Upload (Bluedart / India Post)</h3>
              <p className="text-xs text-slate-500 mb-3">
                CSV columns: <code>awb, our_weight_kg, carrier_weight_kg, carrier_name</code>
              </p>
              <Input type="file" accept=".csv" onChange={onCsvFile} data-testid="csv-file" />
              {csvRows.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs text-slate-600 mb-2">Preview ({csvRows.length} rows)</div>
                  <div className="max-h-60 overflow-y-auto border rounded">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="text-left px-2 py-1">AWB</th>
                          <th className="text-right px-2 py-1">Our</th>
                          <th className="text-right px-2 py-1">Carrier</th>
                          <th className="text-left px-2 py-1">Carrier Name</th>
                        </tr>
                      </thead>
                      <tbody>
                        {csvRows.map((r, i) => (
                          <tr key={i} className="border-b">
                            <td className="px-2 py-1 font-mono">{r.awb}</td>
                            <td className="px-2 py-1 text-right">{r.our_weight_kg}</td>
                            <td className="px-2 py-1 text-right">{r.carrier_weight_kg}</td>
                            <td className="px-2 py-1">{r.carrier_name}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Button onClick={submitCsv} className="mt-3" data-testid="submit-csv">
                    <Upload className="w-4 h-4 mr-1.5" /> Submit Manual Reconciliation
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
        {tab === "delhivery_b2b" && (
          <div className="space-y-4">
            <div className="bg-white border rounded-lg p-4">
              <h3 className="text-sm font-bold text-slate-700 mb-3">Delhivery B2B Weight/Price Reconciliation</h3>
              <div className="flex gap-3 items-end mb-3">
                <div className="flex-1">
                  <label className="text-[10px] uppercase text-slate-500 font-semibold">LR Number</label>
                  <Input value={d2bLrInput} onChange={(e) => setD2bLrInput(e.target.value)}
                         placeholder="e.g. 312450574" data-testid="d2b-lr-input" />
                </div>
                <Button onClick={checkD2bLr} disabled={d2bChecking} className="bg-indigo-600 hover:bg-indigo-700"
                        data-testid="d2b-check-btn">
                  {d2bChecking ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
                  Check Real Price
                </Button>
                <Button onClick={fetchD2bList} variant="outline" disabled={d2bLoading} data-testid="d2b-refresh-btn">
                  Refresh List
                </Button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="text-left px-2 py-2">LR Number</th>
                      <th className="text-right px-2 py-2">Initial Weight</th>
                      <th className="text-right px-2 py-2">Revised Weight</th>
                      <th className="text-right px-2 py-2">Initial Price</th>
                      <th className="text-right px-2 py-2">Revised Price</th>
                      <th className="text-right px-2 py-2">Differential</th>
                      <th className="text-left px-2 py-2">Status</th>
                      <th className="text-left px-2 py-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d2bList.map((row) => (
                      <tr key={row.id} className={`border-b ${
                        row.status === "PENDING_REVIEW" ? "bg-orange-50" :
                        row.status === "APPROVED" ? "bg-emerald-50/50" : ""
                      }`} data-testid={`d2b-row-${row.lrnum}`}>
                        <td className="px-2 py-2 font-mono text-[11px]">{row.lrnum}</td>
                        <td className="px-2 py-2 text-right">{row.initial_weight ?? "—"} kg</td>
                        <td className="px-2 py-2 text-right">{row.revised_weight ?? "—"} kg</td>
                        <td className="px-2 py-2 text-right">{row.initial_amount != null ? `₹${row.initial_amount}` : "—"}</td>
                        <td className="px-2 py-2 text-right">{row.revised_amount != null ? `₹${row.revised_amount}` : "—"}</td>
                        <td className={`px-2 py-2 text-right font-medium ${
                          row.differential > 0 ? "text-red-600" : row.differential < 0 ? "text-emerald-600" : ""
                        }`}>
                          {row.differential != null ? `${row.differential > 0 ? "+" : ""}₹${row.differential}` : "—"}
                        </td>
                        <td className="px-2 py-2">
                          <Badge className={`text-[10px] ${
                            row.status === "PENDING_REVIEW" ? "bg-orange-100 text-orange-700" :
                            row.status === "APPROVED" ? "bg-emerald-100 text-emerald-700" :
                            "bg-slate-100 text-slate-600"
                          }`}>{row.status}</Badge>
                        </td>
                        <td className="px-2 py-2">
                          {row.status === "PENDING_REVIEW" && (
                            <Button size="sm" onClick={() => approveD2b(row.lrnum)} data-testid={`d2b-approve-${row.lrnum}`}>
                              Approve
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {d2bList.length === 0 && (
                      <tr><td colSpan={8} className="text-center text-slate-400 py-6">No records yet — check an LR number above</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* TAB 3: DISPUTES */}
        {tab === "disputes" && (
          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Shipment</th>
                    <th className="text-left px-3 py-2 font-semibold">AWB</th>
                    <th className="text-left px-3 py-2 font-semibold">Raised By</th>
                    <th className="text-left px-3 py-2 font-semibold">Reason</th>
                    <th className="text-right px-3 py-2 font-semibold">Disputed kg</th>
                    <th className="text-left px-3 py-2 font-semibold">Raised At</th>
                    <th className="text-left px-3 py-2 font-semibold">Status</th>
                    <th className="text-right px-3 py-2 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={8} className="py-10 text-center">
                      <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />
                    </td></tr>
                  ) : disputes.length === 0 ? (
                    <tr><td colSpan={8} className="py-10 text-center text-slate-400">No disputes</td></tr>
                  ) : disputes.map((d, i) => (
                    <tr key={i} className="border-b border-slate-100" data-testid={`dispute-row-${i}`}>
                      <td className="px-3 py-2 font-mono text-[11px]">{d.shipment_id}</td>
                      <td className="px-3 py-2 font-mono text-[11px]">{d.awb || "—"}</td>
                      <td className="px-3 py-2">{d.raised_by || "—"}</td>
                      <td className="px-3 py-2 max-w-xs truncate">{d.dispute_reason}</td>
                      <td className="px-3 py-2 text-right">{d.disputed_weight_kg}</td>
                      <td className="px-3 py-2 text-slate-500">
                        {d.raised_at ? new Date(d.raised_at).toLocaleString() : "—"}
                      </td>
                      <td className="px-3 py-2">
                        <Badge className={`text-[10px] ${
                          d.status === "OPEN" ? "bg-red-100 text-red-700" :
                          d.status === "RESOLVED" ? "bg-emerald-100 text-emerald-700" :
                          "bg-slate-100 text-slate-600"
                        }`}>{d.status}</Badge>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button variant="outline" size="sm"
                                onClick={async () => {
                                  await fetch(`${BASE}/disputes/${d.shipment_id}/close`, {
                                    method: "POST", headers,
                                  });
                                  toast.success("Dispute closed");
                                  fetchDisputes(); fetchStats();
                                }}
                                data-testid={`close-${i}`}>
                          Close
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* POD UPLOAD */}
        <div className="bg-white border rounded-lg p-4 mt-4" data-testid="pod-section">
          <h3 className="text-sm font-bold text-slate-700 mb-1">Mark Delivery + Upload POD</h3>
          <p className="text-xs text-slate-500 mb-3">Confirm delivery with OTP (B2C) or signed document (B2B)</p>
          <div className="flex gap-2 mb-3">
            <Input value={podSearch} onChange={(e) => setPodSearch(e.target.value)}
                   onKeyDown={(e) => e.key === "Enter" && searchPodShipment()}
                   placeholder="Enter shipment ID or AWB" className="flex-1"
                   data-testid="pod-search" />
            <Button onClick={searchPodShipment} data-testid="pod-search-btn">
              <Search className="w-4 h-4 mr-1.5" /> Search
            </Button>
          </div>

          {podShipment && (
            <div className="space-y-3">
              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><div className="text-slate-500">AWB</div><div className="font-mono">{podShipment.awb || podShipment.tracking_id}</div></div>
                <div><div className="text-slate-500">Customer</div><div>{podShipment.customer_name || "—"}</div></div>
                <div><div className="text-slate-500">Status</div><div>{(podShipment.status || "—").toUpperCase()}</div></div>
                <div><div className="text-slate-500">Order Type</div><div>{podOrderType}</div></div>
              </div>

              <div className="flex gap-2">
                <Button variant={podOrderType === "B2C" ? "default" : "outline"} size="sm"
                        onClick={() => setPodOrderType("B2C")} data-testid="pod-b2c">B2C POD</Button>
                <Button variant={podOrderType === "B2B" ? "default" : "outline"} size="sm"
                        onClick={() => setPodOrderType("B2B")} data-testid="pod-b2b">B2B POD</Button>
              </div>

              {podOrderType === "B2C" ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Field label="Delivery Photo URL">
                    <Input value={podDeliveryUrl} onChange={(e) => setPodDeliveryUrl(e.target.value)}
                           data-testid="pod-delivery-url" />
                  </Field>
                  <Field label="OTP Code (last 4 of phone)">
                    <Input value={podOtp} onChange={(e) => setPodOtp(e.target.value)}
                           maxLength={6} data-testid="pod-otp" />
                  </Field>
                  <Field label="Customer Phone">
                    <Input value={podPhone} onChange={(e) => setPodPhone(e.target.value)}
                           data-testid="pod-phone" />
                  </Field>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Signed Document URL">
                    <Input value={podSignedUrl} onChange={(e) => setPodSignedUrl(e.target.value)}
                           data-testid="pod-signed-url" />
                  </Field>
                  <Field label="Stamped Paper Photo URL">
                    <Input value={podStampedUrl} onChange={(e) => setPodStampedUrl(e.target.value)}
                           data-testid="pod-stamped-url" />
                  </Field>
                </div>
              )}

              <Button onClick={submitPod} disabled={podSubmitting}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="pod-submit">
                {podSubmitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileText className="w-4 h-4 mr-1.5" />}
                Confirm Delivery + Submit POD
              </Button>
            </div>
          )}
        </div>

        {/* VIEW PROOF MODAL */}
        <Dialog open={!!viewRecon} onOpenChange={() => setViewRecon(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Recon Proof — {viewRecon?.shipment_id}</DialogTitle>
            </DialogHeader>
            {viewRecon && (
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <Detail label="Declared" value={`${viewRecon.declared_weight_kg ?? "—"} kg`} />
                  <Detail label="Actual" value={`${viewRecon.actual_weight_kg ?? "—"} kg`} />
                  <Detail label="Volumetric" value={`${viewRecon.volumetric_weight_kg ?? "—"} kg`} />
                  <Detail label="Chargeable" value={`${viewRecon.chargeable_weight_kg ?? "—"} kg`} />
                  <Detail label="Final" value={`${viewRecon.final_chargeable_weight ?? viewRecon.chargeable_weight_kg ?? "—"} kg`} />
                  <Detail label="Difference" value={`${viewRecon.weight_difference ?? "—"} kg`} />
                </div>
                {viewRecon.weight_photo_url && (
                  <div className="text-xs"><span className="text-slate-500">Weight Photo:</span>{" "}
                    <a href={viewRecon.weight_photo_url} target="_blank" rel="noreferrer" className="text-blue-600 underline">open</a>
                  </div>
                )}
                {viewRecon.midmile_lr_url && (
                  <div className="text-xs"><span className="text-slate-500">Midmile LR:</span>{" "}
                    <a href={viewRecon.midmile_lr_url} target="_blank" rel="noreferrer" className="text-blue-600 underline">open</a>
                  </div>
                )}
                {viewRecon.reweigh_photo_url && (
                  <div className="text-xs"><span className="text-slate-500">Reweigh Photo:</span>{" "}
                    <a href={viewRecon.reweigh_photo_url} target="_blank" rel="noreferrer" className="text-blue-600 underline">open</a>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* DISPUTE MODAL */}
        <Dialog open={disputeOpen} onOpenChange={setDisputeOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Raise Dispute</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Field label="Dispute Reason">
                <Textarea rows={3} value={dReason} onChange={(e) => setDReason(e.target.value)}
                          data-testid="dispute-reason" />
              </Field>
              <Field label="Disputed Weight (kg)">
                <Input type="number" value={dWeight} onChange={(e) => setDWeight(e.target.value)}
                       data-testid="dispute-weight" />
              </Field>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDisputeOpen(false)}>Cancel</Button>
              <Button onClick={doDispute} data-testid="submit-dispute">Submit Dispute</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* REWEIGH MODAL */}
        <Dialog open={reweighOpen} onOpenChange={setReweighOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Reweigh (Free, at destination hub)</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Field label="Reweighed Weight (kg)">
                <Input type="number" value={rwWeight} onChange={(e) => setRwWeight(e.target.value)}
                       data-testid="reweigh-weight" />
              </Field>
              <Field label="Reweigh Photo URL">
                <Input value={rwPhoto} onChange={(e) => setRwPhoto(e.target.value)}
                       placeholder="https://..." data-testid="reweigh-photo" />
              </Field>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReweighOpen(false)}>Cancel</Button>
              <Button onClick={doReweigh} data-testid="submit-reweigh">Submit Reweigh</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white border rounded-lg p-4">
      <div className="text-xs text-slate-500 flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className={`text-2xl font-bold mt-1 ${color}`}>{value}</div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-slate-500 font-semibold">{label}</div>
      <div className="text-sm text-slate-800">{value}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>
      {children}
    </div>
  );
}
